import { Ref, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { Minus, Plus } from 'lucide-react';

import { SurveyQuestion } from 'types/survey';
import { Answer } from 'types/surveyResponse';
import { TextInput } from 'components/ui/TextInput';
import { Button } from 'components/ui/Button';
import styles from './CounterQuestion.module.scss';

interface IProps {
  question: SurveyQuestion;
  answer: Answer;
  onChange: (answer: Answer) => void;
}

export const CounterQuestion = ({ question, answer, onChange }: IProps) => {
  let defaultValue = question.minValue ?? 0;
  if (typeof answer.value === "number") {
    defaultValue = answer.value;
  }
  
  const [value, setValue] = useState(defaultValue.toString());
  const [valid, setValid] = useState(true);
  const onChangeDebounced = useDebouncedCallback(value => onChange({ ...answer, value }), 1500, { maxWait: 10000 });
  const inputRef = useRef<HTMLInputElement>();

  // make sure to do one last update on component unmount
  useEffect(() => {
    return () => {
      onChangeDebounced.flush();
    }
  }, []);

  const onInput = (value: string) => {
    value = value.replace(/\D/g, '');
    setValue(value);
    const intValue = parseInt(value, 10);
    if (isNaN(intValue) || 
      (question.minValue && intValue < question.minValue) ||
      (question.maxValue && intValue > question.maxValue)) {
      setValid(false);
    } else {
      setValid(true);
      onChangeDebounced(intValue);
    }
  };
  
  const onAdd = (add: number) => {
    let intValue = parseInt(value, 10);
    if (isNaN(intValue) ||
      (question.minValue && intValue < question.minValue) ||
      (question.maxValue && intValue > question.maxValue)) {
      if (typeof answer.value === "number") {
        intValue = answer.value;
      } else {
        intValue = defaultValue;
      }
    }

    intValue += add;
    if ((typeof question.minValue !== 'number' || intValue >= question.minValue) &&
      (typeof question.maxValue !== 'number' || intValue <= question.maxValue)) { 
      setValid(true);
      setValue(intValue.toString());
      onChangeDebounced(intValue);
    }
  };

  useEffect(() => {
    inputRef.current!.size = value.length > 0 ? value.length : 1;
  }, [value]);

  return (
    <div className={styles.counterQuestion}>
      <Button variant="secondary" onClick={() => onAdd(-1)}><Minus /></Button>
      <TextInput
        name={'question-' + question.questionId}
        text={value}
        onChange={(e) => onInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp') {
            onAdd(1);
            e.preventDefault();
          }
          if (e.key === 'ArrowDown') {
            onAdd(-1);
            e.preventDefault();
          }
        }}
        state={valid ? undefined : 'error'}
        inputRef={inputRef as Ref<HTMLInputElement | HTMLTextAreaElement>}
      />
      <Button variant="secondary" onClick={() => onAdd(+1)}><Plus /></Button>
    </div>
  );

};
